import { RadioButtonWithLabelOption, TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FormikRadioButtonGroup } from '~commonInfra/components/FormikRadioButtonGroup';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { MetadataDrivenSiteSettings } from '~src/commonInfra/components/MetadataDrivenSiteSetting';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import {
  SiteSettingsForSiteAdminProps
} from '~src/settings/general-settings/components/site-settings/ReactSiteSettingsForSiteAdmin';
import { learnMoreStyle, settingsRadioButtonItemHeight, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { Formatters, Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

export const RECYCLE_BIN_HELP_DOCS_CATEGORY = 'recycle-bin';
export const RECYCLE_BIN_SETTING = 'isRecycleBinEnabled';
export const RECYCLE_BIN_LIFESPAN_SETTING = 'recycleBinContentLifespan';
export const RECYCLE_BIN_ENABLED_FIELD = 'settings.recycleBinSettings.' + RECYCLE_BIN_SETTING;
export const RECYCLE_BIN_LIFESPAN_FIELD = 'settings.recycleBinSettings.' + RECYCLE_BIN_LIFESPAN_SETTING;
export const RECYCLE_BIN_SECTION_NAME = 'recycleBin';
export const RECYCLE_BIN_SECTION_FORMIK_PREFIX = 'settings.recycleBinSettings';
export const RECYCLE_BIN_SECTION_TEST_ID = 'recycle-bin-setting-section';
export const RECYCLE_BIN_ENABLED_CHECKBOX_TEST_ID = 'recycle-bin-enabled';
export const RECYCLE_BIN_LEARN_MORE_LINK_TEST_ID = 'recycle-bin-learn-more';
export const RECYCLE_BIN_LIFESPAN_RADIO_WIDGET_TEST_ID = 'recycle-bin-lifespan';
export const RECYCLE_BIN_LARGE_LIFE_TEST_ID = 'recycle-bin-large-life';
export const RECYCLE_BIN_MEDIUM_LIFE_TEST_ID = 'recycle-bin-medium-life';
export const RECYCLE_BIN_SMALL_LIFE_TEST_ID = 'recycle-bin-small-life';

export type RecycleBinSectionProps = SiteSettingsForSiteAdminProps<'recycleBinSettings'>;

function renderDescription() {
  return (
    <div>
      {Messages.recycleBinSiteSettingsDescription()}
      <TextLinkWidget target={'_blank'}
        href={HelpService.getHelpUrlByCategory(RECYCLE_BIN_HELP_DOCS_CATEGORY)}
        className={learnMoreStyle}
        testId={RECYCLE_BIN_LEARN_MORE_LINK_TEST_ID}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </div>
  );
}

function getRadioButtons(): RadioButtonWithLabelOption<number>[] {
  return [
    {
      value: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_LARGE_LIFE,
      label: Messages.recycleBinSiteSettingsLifeDaysLabel({
        COUNT: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_LARGE_LIFE,
        COUNT_LOC: Formatters.formatCount(RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_LARGE_LIFE)
      }),
      testId: RECYCLE_BIN_LARGE_LIFE_TEST_ID
    },
    {
      value: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_MEDIUM_LIFE,
      label: Messages.recycleBinSiteSettingsLifeDaysLabel({
        COUNT: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_MEDIUM_LIFE,
        COUNT_LOC: Formatters.formatCount(RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_MEDIUM_LIFE)
      }),
      testId: RECYCLE_BIN_MEDIUM_LIFE_TEST_ID
    },
    {
      value: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_SMALL_LIFE,
      label: Messages.recycleBinSiteSettingsLifeDaysLabel({
        COUNT: RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_SMALL_LIFE,
        COUNT_LOC: Formatters.formatCount(RecycleBinUtils.RecycleBinContentLifespans.RECYCLE_BIN_SMALL_LIFE)
      }),
      testId: RECYCLE_BIN_SMALL_LIFE_TEST_ID
    }
  ];
}

export function RecycleBinSettingSection(): React.ReactElement {
  const formik = useFormikContext<RecycleBinSectionProps>();

  if (!FeatureFlags.recycleBinExpEnabled) {
    return null;
  }

  if (FeatureFlags.metadataDrivenSiteSettingsEnabled) {
    return (
      <SettingsSection
        title={Messages.recycleBinBrowserTitle()}
        testId={RECYCLE_BIN_SECTION_TEST_ID}>
        <MetadataDrivenSiteSettings
          sectionName={RECYCLE_BIN_SECTION_NAME}
          formikPrefix={RECYCLE_BIN_SECTION_FORMIK_PREFIX} />
      </SettingsSection>
    );
  }

  const description = renderDescription();
  const buttonList = getRadioButtons();
  let isRecycleBinToggledOn = formik.values.settings.recycleBinSettings?.isRecycleBinEnabled;

  return (
    <SettingsSection
      description={description}
      title={Messages.recycleBinBrowserTitle()}
      testId={RECYCLE_BIN_SECTION_TEST_ID}>
      <FormikCheckbox
        label={Messages.recycleBinSiteSettingsLabel()}
        name={RECYCLE_BIN_ENABLED_FIELD}
        className={SettingsStyles.checkboxGroup}
        testId={RECYCLE_BIN_ENABLED_CHECKBOX_TEST_ID}
      />
      {isRecycleBinToggledOn && (
        <FormikRadioButtonGroup
          label={Messages.recycleBinSiteSettingsLifeLabel()}
          name={RECYCLE_BIN_LIFESPAN_FIELD}
          className={settingsRadioButtonItemHeight}
          buttonList={buttonList}
          testId={RECYCLE_BIN_LIFESPAN_RADIO_WIDGET_TEST_ID}
        />
      )}
    </SettingsSection>
  );
}

export const RecycleBinSettingSectionSpecs = componentRegistryEntry({
  name: 'RecycleBinSettingSection',
  component: RecycleBinSettingSection
});